// Organization settings — Shopify-pattern read-only card layout (mock UI).
//
// Volkan direction 2026-05-23 (Settings 10-section expansion item #1):
// Business details + contact + address + store defaults cards. Edit "..."
// affordance fires informational toast — actual persistence M3 Stage 4
// SPEC.md Faz 1 (memberships + scope_family foundational tables).
//
// Surface-aware via ORG_PROFILES (mocks/data/org-profile.ts):
//   - admin → PEXIFY ADMIN platform record
//   - seller → LeatherCraftLab (Sarah Johnson BO2)
//   - supply → Bursa Ahşap Atölyesi (Mehmet SO2)

import { MoreHorizontal } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { useT } from '../../i18n'
import { organizationI18n } from '../../i18n/settings/organization'
import { settingsShellI18n } from '../../i18n/settings/shell'
import { USE_REAL_API } from '../../lib/api'
import { useToastStore } from '../../lib/toast'
import { orgProfileFor } from '../../lib/maket/org-profile'
import { RealOrganization } from './RealOrganization'
import type { SettingsSurface } from './Settings'

type FieldRow = { label: string; value: string }

function Field({ label, value }: FieldRow) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--color-text-muted)]">
        {label}
      </span>
      <span className="text-sm text-[var(--color-text)]">{value}</span>
    </div>
  )
}

function OrgCard({
  title,
  description,
  rows,
  onEdit,
  editAriaLabel,
}: {
  title: string
  description?: string
  rows: FieldRow[]
  onEdit: () => void
  editAriaLabel: string
}) {
  return (
    <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
      <header className="flex items-start justify-between gap-3 border-b border-[var(--color-border)] px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text)]">{title}</h3>
          {description && (
            <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">{description}</p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label={editAriaLabel}
          onClick={onEdit}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </header>
      <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2">
        {rows.map((r) => (
          <Field key={r.label} label={r.label} value={r.value} />
        ))}
      </div>
    </section>
  )
}

export function Organization({ surface }: { surface: SettingsSurface }) {
  if (USE_REAL_API) return <RealOrganization />
  return <MaketOrganization surface={surface} />
}

function MaketOrganization({ surface }: { surface: SettingsSurface }) {
  const profile = orgProfileFor(surface)
  const push = useToastStore((s) => s.push)
  const t = useT(organizationI18n)
  const shell = useT(settingsShellI18n)

  const notifyMock = (card: string) =>
    push({
      variant: 'info',
      title: `${card} · mock-only`,
      description: t.toastTail,
      duration_ms: 4000,
    })

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t.pageTitle}</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          {t.pageDescriptionLead}
          <span className="font-medium text-[var(--color-text)]">
            {profile.display_name}
          </span>
          {t.pageDescriptionTail}
        </p>
      </header>

      <OrgCard
        title={t.business.title}
        description={t.business.description}
        onEdit={() => notifyMock(t.business.title)}
        editAriaLabel={shell.editAriaLabel(t.business.title)}
        rows={[
          { label: t.business.legalName, value: profile.business_details.legal_name },
          {
            label: t.business.displayName,
            value: profile.business_details.display_name,
          },
          {
            label: t.business.entityType,
            value: profile.business_details.entity_type,
          },
          {
            label: t.business.registrationNumber,
            value: profile.business_details.registration_number,
          },
          { label: t.business.taxId, value: profile.business_details.tax_id },
        ]}
      />

      <OrgCard
        title={t.contact.title}
        description={t.contact.description}
        onEdit={() => notifyMock(t.contact.title)}
        editAriaLabel={shell.editAriaLabel(t.contact.title)}
        rows={[
          { label: t.contact.primaryEmail, value: profile.contact.primary_email },
          { label: t.contact.supportEmail, value: profile.contact.support_email },
          { label: t.contact.phone, value: profile.contact.phone },
        ]}
      />

      <OrgCard
        title={t.address.title}
        description={t.address.description}
        onEdit={() => notifyMock(t.address.title)}
        editAriaLabel={shell.editAriaLabel(t.address.title)}
        rows={[
          { label: t.address.street, value: profile.address.line1 },
          { label: t.address.unit, value: profile.address.line2 },
          { label: t.address.city, value: profile.address.city },
          { label: t.address.region, value: profile.address.region },
          { label: t.address.postalCode, value: profile.address.postal_code },
          { label: t.address.country, value: profile.address.country },
        ]}
      />

      <OrgCard
        title={t.defaults.title}
        description={t.defaults.description}
        onEdit={() => notifyMock(t.defaults.title)}
        editAriaLabel={shell.editAriaLabel(t.defaults.title)}
        rows={[
          { label: t.defaults.currency, value: profile.defaults.currency },
          { label: t.defaults.region, value: profile.defaults.region },
          { label: t.defaults.unitSystem, value: profile.defaults.unit_system },
          { label: t.defaults.weightUnit, value: profile.defaults.weight_unit },
          { label: t.defaults.timezone, value: profile.defaults.timezone },
          { label: t.defaults.language, value: profile.defaults.language },
        ]}
      />

      <p className="text-xs text-[var(--color-text-muted)]">{t.footerNote}</p>
    </div>
  )
}
